import { useState } from 'react';
import { Card, Table, Tag, Select, Space, Button, Modal, Input, Typography, Drawer, Descriptions, DatePicker } from '@/shared/ui/primitives';
import { ReloadOutlined, CheckOutlined, CloseOutlined, EyeOutlined, ClearOutlined } from '@/shared/ui/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { useReports, useReportAction } from '../hooks/useOps';
import type { ReportEntry } from '../api/ops.api';
import { formatDateTime } from '@/shared/utils/formatters';

const { RangePicker } = DatePicker;

const STATUS_COLOR: Record<ReportEntry['status'], string> = {
  PENDING: 'red',
  UNDER_REVIEW: 'orange',
  RESOLVED_ACTION_TAKEN: 'green',
  DISMISSED: 'default',
};

const REASON_COLOR: Record<string, string> = {
  SPAM: 'volcano',
  SCAM: 'red',
  HARASSMENT: 'magenta',
  FAKE_LISTING: 'orange',
  INAPPROPRIATE_CONTENT: 'gold',
  WRONG_INFORMATION: 'blue',
  OTHER: 'default',
};

const ALL_STATUSES: ReportEntry['status'][] = ['PENDING', 'UNDER_REVIEW', 'RESOLVED_ACTION_TAKEN', 'DISMISSED'];
const ALL_TARGETS = ['ROOM_AD', 'ROOM_WANTED_AD', 'USER'] as const;
const ALL_REASONS = ['SPAM', 'SCAM', 'HARASSMENT', 'FAKE_LISTING', 'INAPPROPRIATE_CONTENT', 'WRONG_INFORMATION', 'OTHER'] as const;

export default function ReportsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [filters, setFilters] = useState<any>({ status: 'PENDING' });
  const [detail, setDetail] = useState<ReportEntry | null>(null);
  const [actionModal, setActionModal] = useState<{ report: ReportEntry; status: ReportEntry['status'] } | null>(null);
  const [notes, setNotes] = useState('');

  const params = { page, size, ...filters };
  const { data, isLoading, isFetching, refetch } = useReports(params) as any;
  const action = useReportAction();

  const setFilter = (patch: any) => { setFilters((f: any) => ({ ...f, ...patch })); setPage(0); };

  const confirm = () => {
    if (!actionModal) return;
    action.mutate(
      { id: actionModal.report.id, status: actionModal.status, adminNotes: notes.trim() || undefined },
      {
        onSettled: () => { setActionModal(null); setNotes(''); },
      },
    );
  };

  return (
    <div>
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder={t('reports.filters.status')}
            allowClear
            style={{ width: 220 }}
            value={filters.status}
            options={ALL_STATUSES.map((s) => ({ value: s, label: t(`reports.status.${s}`) }))}
            onChange={(v) => setFilter({ status: v })}
          />
          <Select
            placeholder={t('reports.filters.targetType')}
            allowClear
            style={{ width: 200 }}
            options={ALL_TARGETS.map((v) => ({ value: v, label: t(`reports.targets.${v}`) }))}
            onChange={(v) => setFilter({ targetType: v })}
          />
          <Select
            placeholder={t('reports.filters.reason')}
            allowClear
            style={{ width: 220 }}
            options={ALL_REASONS.map((v) => ({ value: v, label: t(`reports.reasons.${v}`) }))}
            onChange={(v) => setFilter({ reason: v })}
          />
          <RangePicker
            showTime
            onChange={(vals: any) => {
              setFilter({
                createdFrom: vals?.[0]?.toISOString(),
                createdTo: vals?.[1]?.toISOString(),
              });
            }}
          />
          <Button icon={<ClearOutlined />} onClick={() => { setFilters({}); setPage(0); }}>
            {t('common.reset')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            {t('dashboard.refresh')}
          </Button>
        </Space>
      </Card>

      <div className="ds-table-container">
        <Table
          dataSource={data?.content ?? []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: (data?.currentPage ?? 0) + 1,
            pageSize: size,
            total: data?.totalItems ?? 0,
            showSizeChanger: false,
            onChange: (p) => setPage(p - 1),
            showTotal: (t2) => t('reports.count', { count: t2 }),
          }}
          columns={[
            { title: t('common.id'), dataIndex: 'id', width: 70 },
            { title: t('reports.columns.created'), dataIndex: 'createdAt', width: 170, render: (v: string) => formatDateTime(v) },
            {
              title: t('common.status'),
              dataIndex: 'status',
              width: 170,
              render: (s: ReportEntry['status']) => <Tag color={STATUS_COLOR[s]}>{t(`reports.status.${s}`)}</Tag>,
            },
            {
              title: t('reports.columns.target'),
              width: 220,
              render: (_: any, r: ReportEntry) => (
                <Typography.Text code>
                  {t(`reports.targets.${r.targetType}`)}#{r.targetId}
                </Typography.Text>
              ),
            },
            {
              title: t('reports.columns.reason'),
              dataIndex: 'reason',
              width: 200,
              render: (v: string) => <Tag color={REASON_COLOR[v] ?? 'default'}>{t(`reports.reasons.${v}`, v)}</Tag>,
            },
            { title: t('reports.columns.reporter'), dataIndex: 'reporterEmail', width: 220, render: (v: string, r: ReportEntry) => v || `User#${r.reporterId}` },
            { title: t('common.description'), dataIndex: 'description', ellipsis: true },
            {
              title: t('common.actions'),
              width: 280,
              render: (_: any, r: ReportEntry) => {
                const canAct = r.status === 'PENDING' || r.status === 'UNDER_REVIEW';
                return (
                  <Space>
                    <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(r)}>
                      {t('reports.actions.view')}
                    </Button>
                    <Button size="small" type="primary" icon={<CheckOutlined />} disabled={!canAct}
                            onClick={() => setActionModal({ report: r, status: 'RESOLVED_ACTION_TAKEN' })}>
                      {t('reports.actions.action')}
                    </Button>
                    <Button size="small" danger icon={<CloseOutlined />} disabled={!canAct}
                            onClick={() => setActionModal({ report: r, status: 'DISMISSED' })}>
                      {t('reports.actions.dismiss')}
                    </Button>
                  </Space>
                );
              },
            },
          ]}
        />
      </div>

      <Drawer
        width={520}
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? t('reports.detail.heading', { id: detail.id }) : ''}
      >
        {detail && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('reports.columns.created')}>{formatDateTime(detail.createdAt)}</Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag color={STATUS_COLOR[detail.status]}>{t(`reports.status.${detail.status}`)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('reports.columns.target')}>
              {t(`reports.targets.${detail.targetType}`)}#{detail.targetId}
            </Descriptions.Item>
            <Descriptions.Item label={t('reports.columns.reason')}>
              <Tag color={REASON_COLOR[detail.reason] ?? 'default'}>{t(`reports.reasons.${detail.reason}`, detail.reason)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('reports.columns.reporter')}>{detail.reporterEmail || '—'} ({t('common.id')} {detail.reporterId ?? '—'})</Descriptions.Item>
            <Descriptions.Item label={t('common.description')}>{detail.description || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('reports.detail.resolvedBy')}>{detail.resolvedByAdminEmail || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('reports.detail.resolvedAt')}>{formatDateTime(detail.resolvedAt)}</Descriptions.Item>
            <Descriptions.Item label={t('reports.detail.adminNotes')}>{detail.adminNotes || '—'}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      <Modal
        open={!!actionModal}
        onCancel={() => { setActionModal(null); setNotes(''); }}
        onOk={confirm}
        okText={actionModal?.status === 'DISMISSED' ? t('reports.modal.okDismiss') : t('reports.modal.okAction')}
        okButtonProps={{ danger: actionModal?.status === 'DISMISSED', loading: action.isPending }}
        title={actionModal ? (actionModal.status === 'DISMISSED' ? t('reports.modal.dismissTitle') : t('reports.modal.actionTitle')) : ''}
      >
        {actionModal && (
          <p>
            {t('reports.modal.reportOn', {
              id: actionModal.report.id,
              target: `${t(`reports.targets.${actionModal.report.targetType}`)}#${actionModal.report.targetId}`,
            })}
          </p>
        )}
        <Input.TextArea
          rows={4}
          placeholder={t('reports.modal.notesPlaceholder')}
          value={notes}
          onChange={(e: any) => setNotes(e.target.value)}
        />
      </Modal>
    </div>
  );
}
